import { useState } from 'react';

import { Button, ButtonSegment, Input } from '@poseidon/components/Input2';

import { MAX_DAYS, MAX_WEEKS, commitWaitValue, fromDays, reexpressWaitForUnit, type WaitUnit } from './waitDuration';

export type { WaitUnit };

/**
 * Compact wait-duration editor: a number + a days/weeks unit toggle. The value stored on the step is
 * always canonical DAYS (`waitDays`, what the engine waits) — weeks are `value * 7`; `unit` is a display
 * preference. Edits locally while typing and commits (clamped) on blur / Enter / unit change, so we don't
 * fire a save on every keystroke. The days↔weeks math lives in {@link ./waitDuration} (unit-tested).
 */
export function WaitDurationInput({
  days,
  unit,
  onCommit,
}: {
  days: number;
  unit: WaitUnit;
  onCommit: (days: number, unit: WaitUnit) => void;
}) {
  const [value, setValue] = useState(String(fromDays(days, unit)));

  const commit = () => {
    const { value: next, days: canonicalDays } = commitWaitValue(value, unit, days);
    setValue(String(next));
    if (canonicalDays !== days) onCommit(canonicalDays, unit);
  };

  // Re-express the current value in the newly-picked unit (days↔weeks) and commit the switch.
  const switchUnit = (nextUnit: WaitUnit) => {
    if (nextUnit === unit) return;
    const { value: nextValue, days: canonicalDays } = reexpressWaitForUnit(value, unit, nextUnit, days);
    setValue(String(nextValue));
    onCommit(canonicalDays, nextUnit);
  };

  return (
    <div className="flex items-center gap-1.5" onClick={(e) => e.preventDefault()}>
      <Input
        type="number"
        min={1}
        max={unit === 'weeks' ? MAX_WEEKS : MAX_DAYS}
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === 'Enter') e.currentTarget.blur();
        }}
        className="w-14"
      />
      <ButtonSegment size="xs" selectedId={unit} onChange={switchUnit}>
        <Button id="days">days</Button>
        <Button id="weeks">weeks</Button>
      </ButtonSegment>
    </div>
  );
}
